import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  ArrowLeft, 
  Trash2, 
  Plus, 
  MapPin,
  Calendar,
  User,
  Hash,
  Loader2,
  CheckCircle2,
  UserPlus,
  Mail,
  X,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskAssignees, setNewTaskAssignees] = useState([]);
  const [addingTask, setAddingTask] = useState(false);
  const [assigneeDialogOpen, setAssigneeDialogOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [newAssigneeName, setNewAssigneeName] = useState('');
  const [newAssigneeEmail, setNewAssigneeEmail] = useState('');
  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/projects/${id}`, { status });
      setProject(response.data);
      toast.success('Status updated');
      if (status === 'completed') {
        toast.info('Completion emails will be sent to all assignees');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleTaskToggle = async (taskId, completed) => {
    try {
      const response = await axios.put(`${API}/projects/${id}/tasks/${taskId}?completed=${completed}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleAddAssigneeToNewTask = () => {
    if (!newAssigneeName.trim() || !newAssigneeEmail.trim()) {
      toast.error('Please enter both name and email');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newAssigneeEmail)) {
      toast.error('Please enter a valid email');
      return;
    }
    setNewTaskAssignees(prev => [...prev, { name: newAssigneeName, email: newAssigneeEmail }]);
    setNewAssigneeName('');
    setNewAssigneeEmail('');
  };

  const handleRemoveAssigneeFromNewTask = (index) => {
    setNewTaskAssignees(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddTask = async () => {
    if (!newTaskName.trim()) {
      toast.error('Please enter a task name');
      return;
    }
    
    setAddingTask(true);
    try {
      const response = await axios.post(`${API}/projects/${id}/tasks`, {
        name: newTaskName,
        assignees: newTaskAssignees
      });
      setProject(response.data);
      setNewTaskName('');
      setNewTaskAssignees([]);
      setAddTaskDialogOpen(false);
      toast.success('Task added');
      if (newTaskAssignees.length > 0) {
        toast.info('Assignment emails will be sent to assignees');
      }
    } catch (error) {
      toast.error('Failed to add task');
    } finally {
      setAddingTask(false);
    }
  };

  const handleAddAssigneeToTask = async () => {
    if (!newAssigneeName.trim() || !newAssigneeEmail.trim()) {
      toast.error('Please enter both name and email');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newAssigneeEmail)) {
      toast.error('Please enter a valid email');
      return;
    }
    
    const task = project.tasks.find(t => t.id === selectedTaskId);
    if (!task) return;
    
    const updatedAssignees = [...task.assignees, { name: newAssigneeName, email: newAssigneeEmail }];
    
    try {
      const response = await axios.put(`${API}/projects/${id}/tasks/${selectedTaskId}`, null, {
        params: { assignees: JSON.stringify(updatedAssignees) }
      });
      
      // Manual update since params might not work, use body instead
      const res = await axios.put(`${API}/projects/${id}/tasks/${selectedTaskId}`, null, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Refetch to get updated data
      await fetchProject();
      
      setNewAssigneeName('');
      setNewAssigneeEmail('');
      setAssigneeDialogOpen(false);
      toast.success('Assignee added');
      toast.info('Assignment email will be sent');
    } catch (error) {
      // Try alternative approach - update via task endpoint with body
      try {
        await axios({
          method: 'put',
          url: `${API}/projects/${id}/tasks/${selectedTaskId}`,
          data: { assignees: updatedAssignees },
          headers: { 'Content-Type': 'application/json' }
        });
        await fetchProject();
        setNewAssigneeName('');
        setNewAssigneeEmail('');
        setAssigneeDialogOpen(false);
        toast.success('Assignee added');
      } catch (e) {
        toast.error('Failed to add assignee');
      }
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/projects/${id}/tasks/${taskId}`);
      setProject(prev => ({
        ...prev,
        tasks: prev.tasks.filter(t => t.id !== taskId)
      }));
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleDeleteProject = async () => {
    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success('Project deleted');
      navigate('/projects');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      not_started: 'bg-slate-100 text-slate-700',
      in_progress: 'bg-blue-100 text-blue-700',
      on_hold: 'bg-amber-100 text-amber-700',
      completed: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      not_started: 'Not Started',
      in_progress: 'In Progress',
      on_hold: 'On Hold',
      completed: 'Completed'
    };
    return (
      <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!project) return null;

  const completedTasks = project.tasks.filter(t => t.completed).length;
  const totalTasks = project.tasks.length;

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/projects">
          <Button variant="ghost" size="icon" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
              {project.project_number}
            </span>
            <h1 className="font-heading text-2xl font-bold">{project.project_name}</h1>
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="icon" className="text-destructive hover:text-destructive" data-testid="delete-project-button">
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Project</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this project? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteProject} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Project Details */}
        <Card className="lg:col-span-2" data-testid="project-details-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Project Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-muted-foreground">
                  <Hash className="w-4 h-4" />
                  Project Number
                </Label>
                <p className="font-mono">{project.project_number}</p>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  Location
                </Label>
                <p>{project.location}</p>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-muted-foreground">
                  <User className="w-4 h-4" />
                  Coordinator
                </Label>
                <p>{project.coordinator}</p>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  Date Given
                </Label>
                <p>{new Date(project.date_given).toLocaleDateString()}</p>
              </div>
            </div>
            
            <div className="pt-4 border-t">
              <Label className="text-muted-foreground mb-3 block">Status</Label>
              <div className="flex items-center gap-4">
                <Select value={project.status} onValueChange={handleStatusChange} disabled={saving}>
                  <SelectTrigger className="w-48" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              </div>
              {project.completed_at && (
                <p className="text-sm text-muted-foreground mt-2">
                  Completed on {new Date(project.completed_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Task Progress */}
        <Card data-testid="task-progress-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="none"
                    className="text-muted"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${(completedTasks / (totalTasks || 1)) * 352} 352`}
                    strokeLinecap="round"
                    className="text-primary transition-all duration-500"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold">{totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0}%</span>
                  <span className="text-xs text-muted-foreground">{completedTasks}/{totalTasks}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tasks */}
      <Card data-testid="tasks-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            Tasks & Steps
          </CardTitle>
          <Dialog open={addTaskDialogOpen} onOpenChange={setAddTaskDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1" data-testid="add-task-dialog-trigger">
                <Plus className="w-4 h-4" />
                Add Task
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Task</DialogTitle>
                <DialogDescription>
                  Create a task and optionally assign team members. They'll receive email notifications.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="task-name">Task Name *</Label>
                  <Input
                    id="task-name"
                    placeholder="Enter task description..."
                    value={newTaskName}
                    onChange={(e) => setNewTaskName(e.target.value)}
                    data-testid="new-task-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Assignees
                  </Label>
                  
                  {newTaskAssignees.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {newTaskAssignees.map((assignee, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{assignee.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{assignee.email}</p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => handleRemoveAssigneeFromNewTask(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Name"
                      value={newAssigneeName}
                      onChange={(e) => setNewAssigneeName(e.target.value)}
                      className="flex-1"
                      data-testid="assignee-name-input"
                    />
                    <Input
                      placeholder="Email"
                      type="email"
                      value={newAssigneeEmail}
                      onChange={(e) => setNewAssigneeEmail(e.target.value)}
                      className="flex-1"
                      data-testid="assignee-email-input"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="icon"
                      onClick={handleAddAssigneeToNewTask}
                      data-testid="add-assignee-button"
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddTaskDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddTask} disabled={addingTask || !newTaskName.trim()} data-testid="create-task-button">
                  {addingTask ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Task
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Task List */}
          {project.tasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No tasks yet. Click "Add Task" to create your first task.
            </div>
          ) : (
            <div className="space-y-3">
              {project.tasks.map((task) => (
                <div 
                  key={task.id} 
                  className="p-4 rounded-lg border border-border hover:bg-accent/30 transition-colors"
                  data-testid={`task-item-${task.id}`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={task.completed}
                      onCheckedChange={(checked) => handleTaskToggle(task.id, checked)}
                      className="mt-1"
                      data-testid={`task-checkbox-${task.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <span className={`font-medium ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                        {task.name}
                      </span>
                      
                      {/* Assignees */}
                      {task.assignees && task.assignees.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {task.assignees.map((assignee, idx) => (
                            <Badge key={idx} variant="secondary" className="gap-1 text-xs">
                              <User className="w-3 h-3" />
                              {assignee.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Dialog open={assigneeDialogOpen && selectedTaskId === task.id} onOpenChange={(open) => {
                        setAssigneeDialogOpen(open);
                        if (open) setSelectedTaskId(task.id);
                      }}>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-primary"
                            data-testid={`assign-task-${task.id}`}
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Add Assignee</DialogTitle>
                            <DialogDescription>
                              Add a team member to this task. They'll receive an email notification.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label>Current Assignees</Label>
                              {task.assignees && task.assignees.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {task.assignees.map((a, i) => (
                                    <Badge key={i} variant="outline" className="gap-1">
                                      <Mail className="w-3 h-3" />
                                      {a.name}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">No assignees yet</p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <Label>New Assignee</Label>
                              <Input
                                placeholder="Name"
                                value={newAssigneeName}
                                onChange={(e) => setNewAssigneeName(e.target.value)}
                              />
                              <Input
                                placeholder="Email"
                                type="email"
                                value={newAssigneeEmail}
                                onChange={(e) => setNewAssigneeEmail(e.target.value)}
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setAssigneeDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button onClick={handleAddAssigneeToTask}>
                              Add Assignee
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteTask(task.id)}
                        data-testid={`delete-task-${task.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
